import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Trash2, CheckCircle2, Undo2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";

const FILTERS = [
  { key: "waiting", label: "Waiting on me" },
  { key: "replied", label: "Replied" },
  { key: "all", label: "All" },
];

const MessageCard = ({ m, onToggle, onDelete }) => (
  <div className={`silk-card rounded-lg p-5 ${m.replied ? "opacity-70" : ""}`} data-testid={`message-${m.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <p className="text-sm text-foreground">{m.name}
        <a href={`mailto:${m.email}`} className="text-primary ml-2 hover:underline">{m.email}</a>
      </p>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${m.replied ? "border-border text-muted-foreground" : "border-primary/50 text-primary"}`} data-testid={`message-status-${m.id}`}>
          {m.replied ? "Replied" : "Waiting"}
        </span>
        <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</span>
      </div>
    </div>
    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{m.message}</p>
    <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
      <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
        <Mail size={12} /> Reply by email
      </a>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground hover:text-foreground" data-testid={`message-replied-btn-${m.id}`} onClick={() => onToggle(m)}>
          {m.replied ? <><Undo2 size={13} className="mr-1.5" /> Mark as waiting</> : <><CheckCircle2 size={13} className="mr-1.5" /> Mark as replied</>}
        </Button>
        <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground hover:text-foreground" data-testid={`message-delete-btn-${m.id}`} onClick={() => onDelete(m)}>
          <Trash2 size={13} className="mr-1.5" /> Delete
        </Button>
      </div>
    </div>
  </div>
);

export const MessagesInbox = ({ onChange }) => {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("waiting");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data));
  useEffect(() => { load(); }, []);

  const toggleReplied = async (m) => {
    try {
      const { data } = await api.patch(`/admin/messages/${m.id}/replied`, { replied: !m.replied });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
      toast.success(data.replied ? "Marked as replied" : "Back in your waiting list");
      onChange?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/messages/${target.id}`);
      toast.success("Message deleted");
      setTarget(null);
      load();
      onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const counts = { waiting: messages.filter((m) => !m.replied).length, replied: messages.filter((m) => m.replied).length, all: messages.length };
  const visible = messages.filter((m) => filter === "all" || (filter === "replied") === !!m.replied);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <h2 className="font-display text-lg md:text-2xl text-foreground">Contact messages</h2>
        <div className="flex flex-wrap gap-2" data-testid="messages-filters">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`messages-filter-${f.key}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === f.key ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {f.label} · {counts[f.key]}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-3" data-testid="messages-inbox">
        {visible.map((m) => <MessageCard key={m.id} m={m} onToggle={toggleReplied} onDelete={setTarget} />)}
        {visible.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="messages-empty">
            {filter === "waiting" && messages.length > 0 ? "You're all caught up — nothing waiting on you. ✦" : "No messages here yet."}
          </p>
        )}
      </div>

      <ConfirmDeleteDialog open={!!target} onOpenChange={() => setTarget(null)} busy={busy} onConfirm={confirmDelete}
        testid="message-delete-dialog" title={`Delete the message from ${target?.name || target?.email}?`}
        description="This removes the message from your inbox for good. The sender isn't notified. This can't be undone."
        confirmLabel="Delete message" cancelLabel="Keep message" />
    </div>
  );
};
